import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ProductPrice, SoldBadge, StockStatus } from './ProductBits';
import type { Product } from '../../data/products';

/**
 * One shop item, linking to its own page.
 *
 * Stretched link: the whole card is a single control whose accessible name
 * is the product name, with no nested interactive elements. The "View item"
 * row is `aria-hidden` because it is an affordance for the link the heading
 * already provides, not a second destination.
 *
 * The pending-photograph note sits inside the image frame rather than in
 * the body, so it reads as a caveat on the picture it applies to.
 *
 * No status pills here. They led every card with the same row of shouty
 * uppercase chips before the name, and `StockStatus` in the footer already
 * carries the one fact that changes a decision. The full label set stays on
 * the product page, where there is room for it to mean something.
 */
export function ProductCard({
  product
}: {
  product: Product;
}) {
  return <article data-component-id="C19" className="group relative flex h-full flex-col overflow-hidden rounded-container border border-hairline bg-white transition-[border-color,box-shadow] duration-200 ease-out-expo focus-within:border-rust focus-within:shadow-lg hover:border-rust hover:shadow-lg" data-id="element-158" data-mp="2hcuh5eveaeld">
      <div className="relative aspect-square overflow-hidden border-b border-hairline bg-sand" data-id="element-159" data-mp="22k0pkv2um80c">
        <img src={product.image} alt={product.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-out-expo group-hover:scale-[1.03] motion-reduce:transform-none" data-id="element-160" data-mp="3079g54hpi797" />
        {product.sold && <div className="absolute right-3 top-3" data-id="element-161" data-mp="1ew11k2wx8vpg">
            <SoldBadge data-id="element-162" data-mp="1exu7ffzpnbwe" />
          </div>}
        {/* Says so plainly when the picture is standing in for the piece,
            rather than letting a rail of tees pass for a piston clock. */}
        {product.photoPending && <p className="absolute inset-x-0 bottom-0 bg-ink/85 px-3 py-1.5 text-center text-[11px] font-bold uppercase tracking-[0.08em] text-cream" data-id="element-163" data-mp="1haxp0cvd7cye">
            Photo of this one coming soon
          </p>}
      </div>

      <div className="flex flex-1 flex-col p-5" data-id="element-164" data-mp="17bysttrsd9sa">
        <h3 className="text-base font-bold leading-snug text-ink" data-id="element-165" data-mp="17euqri3mvygq">
          <Link to={`/shop/${product.slug}`} className="after:absolute after:inset-0 after:rounded-container after:content-[''] focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-rust focus-visible:after:ring-offset-2" data-id="element-166" data-mp="tklauk9j0jzd">
            {product.name}
          </Link>
        </h3>

        {product.makerName && <p className="mt-1 text-xs font-medium text-ink-muted" data-id="element-167" data-mp="amr1g54hk6gg">
            by {product.makerName}
          </p>}

        <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/80" data-id="element-168" data-mp="en1axrrhlf02">
          {product.shortDescription}
        </p>

        <div className="mt-4 flex items-center justify-between gap-3 border-t border-hairline pt-4" data-id="element-169" data-mp="3s5pwv44j91dh">
          <ProductPrice price={product.price} className="text-2xl" data-id="element-170" data-mp="3n7xwdlpto6xo" />
          <StockStatus product={product} data-id="element-171" data-mp="51jjzr77c51e" />
        </div>

        <span aria-hidden="true" className="mt-4 flex items-center gap-1.5 text-sm font-bold text-ink transition-colors duration-200 ease-out-expo group-hover:text-rust" data-id="element-172" data-mp="4y46gst6f423">
          View item
          <ArrowRight className="h-4 w-4 transition-transform duration-200 ease-out-expo group-hover:translate-x-1 motion-reduce:transform-none" data-id="element-173" data-mp="a0389y99pswx" />
        </span>
      </div>
    </article>;
}